import type { CalendarRef, DatesSetInfo, EventClickInfo, EventInput } from "@fullcalendar/react";
import "@fullcalendar/react/skeleton.css";
import "@fullcalendar/react/themes/classic/theme.css";
import { AlertCircle, CalendarDays, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ComponentType, FormEvent } from "react";
import type { SwitcherUser } from "../components/AppSwitcher";
import { AppContainer } from "../components/AppContainer";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { realtimeFetch } from "../realtime/client";
import { useRealtime } from "../realtime/useRealtime";
import {
  CALENDAR_COLORS,
  calendarDate,
  calendarDay,
  calendarEventOccursOn,
  calendarRangeDates,
  isCalendarDate,
  isCalendarTime,
  isRepeatingCalendarEvent,
  localCalendarDate,
  startOfSundayWeek,
} from "./domain";
import type {
  CalendarColor,
  CalendarEventInput,
  CalendarEventSeries,
  CalendarOccurrence,
  CalendarRepeatType,
} from "./domain";
import type { FullCalendarWeekGridProps } from "./FullCalendarWeekGrid";

type ErrorBody = { error?: { message?: string; fields?: Array<{ message?: string }> } };
const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const colorNames: Record<CalendarColor, string> = { blue: "Blue", green: "Green", orange: "Orange", purple: "Purple", pink: "Pink", red: "Red", teal: "Teal", slate: "Slate" };
const repeatNames: Record<CalendarRepeatType, string> = { none: "Single", daily: "Daily", weekly: "Weekly", monthly: "Monthly", days: "Every number of days" };
const goTo = (path: string) => window.location.assign(path);

export function calendarEditorActionLabels(editing: boolean, _repeating: boolean) {
  if (!editing) return { save: "Add event", remove: "Delete event", confirm: "Delete event" };
  return { save: "", remove: "Delete event", confirm: "Delete event" };
}

export function formatCalendarExceptionDate(date: string): string {
  if (!isCalendarDate(date)) return "occurrence";
  const [year, month, day] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(year!, month! - 1, day!)));
}

export function CalendarExceptionControls({ series, busy, onAdd, onUpdate, onDelete }: {
  series: CalendarEventSeries;
  busy: string | null;
  onAdd: () => void;
  onUpdate: (currentDate: string, date: string, days: number) => void;
  onDelete: (date: string) => void;
}) {
  if (!isRepeatingCalendarEvent(series)) return null;
  return <fieldset className="form-field calendar-exceptions">
    <legend className="calendar-exceptions-legend"><span>Skipped dates</span><button className="secondary-button calendar-exception-add-button" type="button" disabled={busy !== null} onClick={onAdd}><Plus size={16} />{busy === "add" ? "Adding…" : "Add"}</button></legend>
    <div className="calendar-exception-listbox">
      <div className="calendar-exception-list-heading" aria-hidden="true"><span>Date</span><span>Days</span><span /></div>
      {series.skippedRanges.length === 0
        ? <p className="calendar-exceptions-empty">No skipped dates.</p>
        : <ul className="calendar-exception-list">{series.skippedRanges.map(range => <li key={range.date}><input aria-label={`Start date for skipped dates beginning ${formatCalendarExceptionDate(range.date)}`} type="date" value={range.date} disabled={busy !== null} onInput={event => onUpdate(range.date, event.currentTarget.value, range.days)} /><input aria-label={`Days to skip beginning ${formatCalendarExceptionDate(range.date)}`} type="number" min={1} max={3650} step={1} value={range.days} disabled={busy !== null} onChange={event => onUpdate(range.date, range.date, Number(event.currentTarget.value))} /><button className="secondary-button" type="button" disabled={busy !== null} onClick={() => onDelete(range.date)} aria-label={`Delete skipped dates starting ${formatCalendarExceptionDate(range.date)}`}><Trash2 size={16} /></button></li>)}</ul>}
    </div>
  </fieldset>;
}

async function loadFullCalendarWeekGrid() {
  if (typeof window === "undefined" || typeof window.ResizeObserver === "undefined") return import("./FullCalendarWeekGrid");
  const NativeResizeObserver = window.ResizeObserver;
  class DeferredResizeObserver extends NativeResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => requestAnimationFrame(() => callback(entries, observer)));
    }
  }
  window.ResizeObserver = DeferredResizeObserver;
  try { return await import("./FullCalendarWeekGrid"); }
  finally { window.ResizeObserver = NativeResizeObserver; }
}

async function responseError(response: Response) {
  const body = await response.json().catch(() => null) as ErrorBody | null;
  return body?.error?.fields?.[0]?.message ?? body?.error?.message ?? "Something went wrong. Please try again.";
}

export function calendarWeekRange(date: string) {
  const start = startOfSundayWeek(date);
  return { start, end: calendarDate(calendarDay(start) + 6) };
}

export function occurrenceEventInput(item: CalendarOccurrence): EventInput {
  return {
    id: item.id,
    title: item.name,
    start: item.start,
    end: item.end,
    color: CALENDAR_COLORS[item.color],
    contrastColor: "#1f2937",
    extendedProps: { seriesId: item.seriesId },
  };
}

export function calendarEventEditHref(seriesId: number, occurrenceStart: Date): string {
  return `/calendar/${seriesId}/edit?date=${localCalendarDate(occurrenceStart)}`;
}

function selectedDateFromLocation() {
  const value = new URLSearchParams(window.location.search).get("date");
  return isCalendarDate(value) ? value : localCalendarDate();
}

function CalendarWeek({ user, onSignOut }: { user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const initialDate = useMemo(selectedDateFromLocation, []);
  const calendarRef = useRef<CalendarRef>(null);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [range, setRange] = useState(() => calendarWeekRange(initialDate));
  const [occurrences, setOccurrences] = useState<CalendarOccurrence[]>([]);
  const [CalendarGrid, setCalendarGrid] = useState<ComponentType<FullCalendarWeekGridProps> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const pickerDate = `${selectedDate.slice(5, 7)} / ${selectedDate.slice(8, 10)} / ${selectedDate.slice(0, 4)}`;

  useEffect(() => {
    let active = true;
    void loadFullCalendarWeekGrid().then(module => { if (active) setCalendarGrid(() => module.default); });
    return () => { active = false; };
  }, []);

  async function load(nextRange = range, spinner = true) {
    if (spinner) setLoading(true);
    try {
      const response = await fetch(`/api/calendar/events?start=${nextRange.start}&end=${nextRange.end}`, { cache: "no-store" });
      if (!response.ok) throw new Error(await responseError(response));
      const data = await response.json() as { occurrences: CalendarOccurrence[] };
      setOccurrences(data.occurrences);
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the calendar.");
    } finally {
      if (spinner) setLoading(false);
    }
  }

  useEffect(() => { void load(range); }, [range.start, range.end]);
  useRealtime("calendar", () => void load(range, false));

  function showDate(value: string) {
    if (!isCalendarDate(value)) return;
    setSelectedDate(value);
    window.history.replaceState(null, "", `/calendar/?date=${value}`);
    calendarRef.current?.getApi().gotoDate(value);
  }

  function handleDatesSet(info: DatesSetInfo) {
    const next = {
      start: localCalendarDate(info.start),
      end: calendarDate(calendarDay(localCalendarDate(info.end)) - 1),
    };
    setRange(current => current.start === next.start && current.end === next.end ? current : next);
  }

  function handleEventClick(info: EventClickInfo) {
    const seriesId = Number(info.event.extendedProps.seriesId);
    if (Number.isSafeInteger(seriesId) && seriesId > 0 && info.event.start) goTo(calendarEventEditHref(seriesId, info.event.start));
  }

  return (
    <AppContainer
      className="calendar-shell app-calendar"
      contentClassName="calendar-scroll"
      currentDestination="calendar"
      user={user}
      onSignOut={onSignOut}
      asHeading
      actions={<><label className="calendar-date-picker"><CalendarDays size={18} aria-hidden="true" /><span className="calendar-date-picker-value" aria-hidden="true">{pickerDate}</span><input aria-label="Choose date" type="date" value={selectedDate} onInput={event => showDate(event.currentTarget.value)} /></label><a className="add-chore-button calendar-add-button" href={`/calendar/new?date=${selectedDate}`} aria-label="Add event"><Plus size={25} /></a></>}
    >
      {error && <div className="inline-error calendar-error" role="alert"><AlertCircle size={18} />{error}<button type="button" onClick={() => void load(range)}>Try again</button></div>}
      <div className="calendar-grid-scroll" data-horizontal-scroll="true">
        <div className="calendar-grid-frame">
          {CalendarGrid
            ? <CalendarGrid calendarRef={calendarRef} initialDate={initialDate} events={occurrences.map(occurrenceEventInput)} onDatesSet={handleDatesSet} onEventClick={handleEventClick} />
            : <div className="calendar-state">Preparing week…</div>}
          {loading && <div className="calendar-loading" role="status">Loading week…</div>}
          {!loading && !error && occurrences.length === 0 && <div className="calendar-empty-note">No events this week.</div>}
        </div>
      </div>
    </AppContainer>
  );
}

function newEventInput(today: string): CalendarEventInput {
  return {
    name: "",
    startDate: today,
    endDate: today,
    startTime: "09:00",
    endTime: "10:00",
    color: "blue",
    repeatType: "none",
    weeklyDays: [new Date(`${today}T12:00:00`).getDay()],
    monthlyDay: Number(today.slice(8, 10)),
    intervalDays: 1,
  };
}

function CalendarEditor({ id, user, onSignOut }: { id: number | null; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const editing = id !== null;
  const returnDate = selectedDateFromLocation();
  const [form, setForm] = useState<CalendarEventInput>(() => newEventInput(localCalendarDate()));
  const [savedEvent, setSavedEvent] = useState<CalendarEventSeries | null>(null);
  const [exceptionDate, setExceptionDate] = useState(returnDate);
  const [exceptionBusy, setExceptionBusy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(!editing);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const saveTimer = useRef<number | undefined>(undefined);
  const snapshot = useRef(form);
  const lastSaved = useRef("");
  const revision = useRef(0);
  const saveTail = useRef(Promise.resolve(true));
  const pendingSaves = useRef(0);
  const deletingRef = useRef(false);
  const leavingRef = useRef(false);
  const backHref = `/calendar/?date=${returnDate}`;

  useEffect(() => {
    if (!editing) return;
    void (async () => {
      try {
        const response = await fetch(`/api/calendar/events/${id}`, { cache: "no-store" });
        if (!response.ok) throw new Error(await responseError(response));
        const { event } = await response.json() as { event: CalendarEventSeries };
        const loadedForm = { name: event.name, startDate: event.startDate, endDate: event.endDate, startTime: event.startTime, endTime: event.endTime, color: event.color, repeatType: event.repeatType, weeklyDays: event.weeklyDays, monthlyDay: event.monthlyDay, intervalDays: event.intervalDays };
        setSavedEvent(event);
        snapshot.current = loadedForm;
        lastSaved.current = JSON.stringify(loadedForm);
        setForm(loadedForm);
        setStatus("");
        setExceptionDate(calendarEventOccursOn(event, returnDate) ? returnDate : event.startDate);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Could not load this event.");
      } finally {
        setLoaded(true);
      }
    })();
  }, [editing, id]);

  useEffect(() => () => window.clearTimeout(saveTimer.current), []);

  function validationMessage(value: CalendarEventInput): string {
    if (!value.name.trim()) return "Name is required.";
    if (value.name.trim().length > 120) return "Name must be 120 characters or fewer.";
    if (!isCalendarDate(value.startDate)) return "Start date must be a valid date.";
    if (value.endDate !== null && !isCalendarDate(value.endDate)) return "End date must be a valid date or blank.";
    if (!isCalendarTime(value.startTime)) return "Start time must be valid.";
    if (!isCalendarTime(value.endTime)) return "End time must be valid.";
    if (value.endTime <= value.startTime) return "End time must be later than start time.";
    if (value.repeatType === "weekly" && value.weeklyDays.length === 0) return "Choose one or more weekdays.";
    if (value.repeatType === "monthly" && (!Number.isInteger(value.monthlyDay) || value.monthlyDay! < 1 || value.monthlyDay! > 31)) return "Day of month must be a whole number from 1 to 31.";
    if (value.repeatType === "days" && (!Number.isSafeInteger(value.intervalDays) || value.intervalDays! < 1 || value.intervalDays! > 3650)) return "Day interval must be a whole number from 1 to 3650.";
    return "";
  }

  function enqueueSave(value: CalendarEventInput, saveRevision: number) {
    pendingSaves.current += 1;
    const task = saveTail.current.then(async () => {
      if (deletingRef.current) return false;
      const response = await realtimeFetch(`/api/calendar/events/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(value) });
      if (!response.ok) throw new Error(await responseError(response));
      const { event } = await response.json() as { event: CalendarEventSeries };
      lastSaved.current = JSON.stringify(value);
      if (saveRevision === revision.current) { setSavedEvent(event); setStatus(""); setError(""); }
      return true;
    }).catch(caught => {
      if (saveRevision === revision.current && !deletingRef.current) { setStatus(""); setError(caught instanceof Error ? caught.message : "Could not save this event."); }
      return false;
    }).finally(() => { pendingSaves.current -= 1; });
    saveTail.current = task;
    return task;
  }

  function scheduleSave(value: CalendarEventInput) {
    window.clearTimeout(saveTimer.current);
    if (!editing) return;
    const saveRevision = ++revision.current;
    const issue = validationMessage(value);
    if (issue) { setStatus(""); setError(issue); return; }
    if (JSON.stringify(value) === lastSaved.current && pendingSaves.current === 0) { setStatus(""); return; }
    setStatus("Saving…");
    saveTimer.current = window.setTimeout(() => { saveTimer.current = undefined; void enqueueSave(value, saveRevision); }, 500);
  }

  function change<K extends keyof CalendarEventInput>(key: K, value: CalendarEventInput[K]) {
    if (deletingRef.current || leavingRef.current) return;
    const next = { ...snapshot.current, [key]: value };
    snapshot.current = next;
    setForm(next);
    setError("");
    scheduleSave(next);
  }

  function changeRepeatType(repeatType: CalendarRepeatType) {
    if (deletingRef.current || leavingRef.current) return;
    const current = snapshot.current;
    const next = { ...current, repeatType, endDate: current.repeatType === "none" && repeatType !== "none" ? null : repeatType === "none" && current.endDate === null ? current.startDate : current.endDate };
    snapshot.current = next;
    setForm(next);
    setError("");
    scheduleSave(next);
  }

  function toggleWeekday(day: number) {
    const currentDays = snapshot.current.weeklyDays;
    const next = currentDays.includes(day) ? currentDays.filter(value => value !== day) : [...currentDays, day].sort((a, b) => a - b);
    change("weeklyDays", next);
  }

  function singleDays(value = snapshot.current): number {
    if (!isCalendarDate(value.startDate) || !isCalendarDate(value.endDate)) return 1;
    return Math.max(1, calendarDay(value.endDate) - calendarDay(value.startDate) + 1);
  }

  function changeSingleStart(startDate: string) {
    if (!isCalendarDate(startDate)) { change("startDate", startDate); return; }
    const current = snapshot.current;
    const next = { ...current, startDate, endDate: calendarDate(calendarDay(startDate) + singleDays(current) - 1) };
    snapshot.current = next;
    setForm(next);
    setError("");
    scheduleSave(next);
  }

  function changeSingleDays(days: number) {
    if (!Number.isSafeInteger(days) || days < 1 || days > 3650 || !isCalendarDate(snapshot.current.startDate)) return;
    change("endDate", calendarDate(calendarDay(snapshot.current.startDate) + days - 1));
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving || exceptionBusy || editing) return;
    setSaving(true); setError("");
    try {
      const response = await realtimeFetch("/api/calendar/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error(await responseError(response));
      goTo(`/calendar/?date=${form.startDate}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save this event.");
      setSaving(false);
    }
  }

  async function remove() {
    if (!editing || deleting || exceptionBusy) return;
    deletingRef.current = true;
    window.clearTimeout(saveTimer.current);
    revision.current += 1;
    setDeleting(true); setError("");
    try {
      await saveTail.current;
      const response = await realtimeFetch(`/api/calendar/events/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await responseError(response));
      goTo(backHref);
    } catch (caught) {
      deletingRef.current = false;
      setError(caught instanceof Error ? caught.message : "Could not delete this event.");
      setDeleting(false); setConfirmDelete(false);
    }
  }

  async function back() {
    if (!editing || deletingRef.current || leavingRef.current) return;
    window.clearTimeout(saveTimer.current);
    const current = snapshot.current;
    const issue = validationMessage(current);
    if (issue) { setStatus(""); setError(issue); return; }
    leavingRef.current = true;
    const saveRevision = ++revision.current;
    setStatus("Saving…");
    const saved = JSON.stringify(current) === lastSaved.current && pendingSaves.current === 0 ? await saveTail.current : await enqueueSave(current, saveRevision);
    if (saved) goTo(backHref);
    else leavingRef.current = false;
  }

  function eventWithSkippedRanges(event: CalendarEventSeries, skippedRanges: CalendarEventSeries["skippedRanges"]): CalendarEventSeries {
    return { ...event, skippedRanges, skippedDates: [...new Set(skippedRanges.flatMap(range => calendarRangeDates(range.date, range.days)))].sort() };
  }

  async function addSkippedRange() {
    if (!editing || !savedEvent || exceptionBusy || !isCalendarDate(exceptionDate)) return;
    const usedDates = new Set(savedEvent.skippedRanges.map(range => range.date));
    let date = exceptionDate;
    while (usedDates.has(date)) date = calendarDate(calendarDay(date) + 1);
    const previous = savedEvent;
    setSavedEvent(eventWithSkippedRanges(savedEvent, [...savedEvent.skippedRanges, { date, days: 1 }].sort((left, right) => left.date.localeCompare(right.date))));
    setExceptionBusy("add"); setError("");
    try {
      const response = await realtimeFetch(`/api/calendar/events/${id}/exceptions`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ date, days: 1 }) });
      if (!response.ok) throw new Error(await responseError(response));
      const { event } = await response.json() as { event: CalendarEventSeries };
      setSavedEvent(event);
      setExceptionDate(calendarDate(calendarDay(date) + 1));
      setExceptionBusy(null);
    } catch (caught) {
      setSavedEvent(previous);
      setError(caught instanceof Error ? caught.message : "Could not add these skipped dates.");
      setExceptionBusy(null);
    }
  }

  async function updateSkippedRange(currentDate: string, date: string, days: number) {
    if (!editing || !savedEvent || exceptionBusy || !isCalendarDate(date) || !Number.isSafeInteger(days) || days < 1 || days > 3650) return;
    const previous = savedEvent;
    const ranges = savedEvent.skippedRanges.map(range => range.date === currentDate ? { date, days } : range).sort((left, right) => left.date.localeCompare(right.date));
    setSavedEvent(eventWithSkippedRanges(savedEvent, ranges));
    setExceptionBusy(currentDate); setError("");
    try {
      const response = await realtimeFetch(`/api/calendar/events/${id}/exceptions/${currentDate}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ date, days }) });
      if (!response.ok) throw new Error(await responseError(response));
      const { event } = await response.json() as { event: CalendarEventSeries };
      setSavedEvent(event);
      setExceptionBusy(null);
    } catch (caught) {
      setSavedEvent(previous);
      setError(caught instanceof Error ? caught.message : "Could not update these skipped dates.");
      setExceptionBusy(null);
    }
  }

  async function deleteSkippedRange(date: string) {
    if (!editing || !savedEvent || exceptionBusy) return;
    const previous = savedEvent;
    setSavedEvent(eventWithSkippedRanges(savedEvent, savedEvent.skippedRanges.filter(range => range.date !== date)));
    setExceptionBusy(date); setError("");
    try {
      const response = await realtimeFetch(`/api/calendar/events/${id}/exceptions/${date}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await responseError(response));
      const { event } = await response.json() as { event: CalendarEventSeries };
      setSavedEvent(event);
      setExceptionBusy(null);
    } catch (caught) {
      setSavedEvent(previous);
      setError(caught instanceof Error ? caught.message : "Could not delete these skipped dates.");
      setExceptionBusy(null);
    }
  }

  if (!loaded) return <AppContainer className="editor-shell app-calendar" currentDestination="calendar" user={user} onSignOut={onSignOut} backHref={backHref} title="Edit event" asHeading><div className="calendar-state">Loading event…</div></AppContainer>;

  const repeating = editing && isRepeatingCalendarEvent(form);
  const actionLabels = calendarEditorActionLabels(editing, repeating);

  return (
    <AppContainer className="editor-shell calendar-editor-shell app-calendar" currentDestination="calendar" user={user} onSignOut={onSignOut} backHref={backHref} onBack={editing ? () => void back() : undefined} title={editing ? "Edit event" : "New event"} actions={editing && (status === "Saving…" || exceptionBusy !== null) ? <span className="calendar-saving-status" role="status">Saving…</span> : undefined} asHeading>
      <form className="chore-form calendar-form" autoComplete="off" onSubmit={save}>
        {error && <div className="form-error" role="alert">{error}</div>}
        <label className="form-field"><span>Name</span><input autoFocus={!editing} name="event-name" maxLength={120} value={form.name} onChange={event => change("name", event.target.value)} required /></label>
        <div className="calendar-form-grid">
          <label className="form-field"><span>Start time</span><input name="start-time" type="time" value={form.startTime} onInput={event => change("startTime", event.currentTarget.value)} required /></label>
          <label className="form-field"><span>End time</span><input name="end-time" type="time" value={form.endTime} onInput={event => change("endTime", event.currentTarget.value)} required /></label>
        </div>
        <fieldset className="form-field calendar-color-field"><legend>Color</legend><div className="calendar-colors">{(Object.keys(CALENDAR_COLORS) as CalendarColor[]).map(color => <label className="calendar-color" key={color} title={colorNames[color]}><input type="radio" name="color" value={color} checked={form.color === color} onChange={() => change("color", color)} /><span style={{ backgroundColor: CALENDAR_COLORS[color] }} aria-hidden="true" /><span className="sr-only">{colorNames[color]}</span></label>)}</div></fieldset>
        <label className="form-field"><span>Repeat</span><select name="repeat-type" value={form.repeatType} onChange={event => changeRepeatType(event.target.value as CalendarRepeatType)}>{(Object.keys(repeatNames) as CalendarRepeatType[]).map(type => <option value={type} key={type}>{repeatNames[type]}</option>)}</select></label>
        {form.repeatType === "none" && <div className="calendar-form-grid"><label className="form-field"><span>Start</span><input name="start-date" type="date" value={form.startDate} onInput={event => changeSingleStart(event.currentTarget.value)} required /></label><label className="form-field"><span>Days</span><input name="single-days" type="number" min={1} max={3650} step={1} value={singleDays(form)} onChange={event => changeSingleDays(Number(event.currentTarget.value))} required /></label></div>}
        {form.repeatType === "weekly" && <fieldset className="form-field"><legend>Repeat on</legend><div className="calendar-weekdays">{weekdayNames.map((name, day) => <label key={name}><input type="checkbox" checked={form.weeklyDays.includes(day)} onChange={() => toggleWeekday(day)} /><span>{name}</span></label>)}</div></fieldset>}
        {form.repeatType === "monthly" && <label className="form-field"><span>Day of month</span><input type="number" name="monthly-day" min={1} max={31} step={1} value={form.monthlyDay ?? ""} onChange={event => change("monthlyDay", event.target.value ? Number(event.target.value) : null)} required /></label>}
        {form.repeatType === "days" && <label className="form-field"><span>Number of days between events</span><input type="number" name="interval-days" min={1} max={3650} step={1} value={form.intervalDays ?? ""} onChange={event => change("intervalDays", event.target.value ? Number(event.target.value) : null)} required /></label>}
        {savedEvent && form.repeatType !== "none" && <CalendarExceptionControls series={{ ...savedEvent, ...form }} busy={exceptionBusy} onAdd={() => void addSkippedRange()} onUpdate={(currentDate, date, days) => void updateSkippedRange(currentDate, date, days)} onDelete={date => void deleteSkippedRange(date)} />}
        {editing ? <div className="calendar-live-actions"><button className="editor-delete-button" type="button" disabled={deleting || exceptionBusy !== null} onClick={() => setConfirmDelete(true)}><Trash2 size={17} />Delete event</button></div> : <div className="calendar-form-actions"><button className="primary-button" type="submit" disabled={saving || deleting || exceptionBusy !== null}>{saving ? "Saving…" : actionLabels.save}</button></div>}
      </form>
      {confirmDelete && <DeleteConfirmDialog title={repeating ? <>Delete the entire “{form.name || "this event"}” series?</> : <>Delete “{form.name || "this event"}”?</>} titleId="delete-calendar-event-title" description={repeating ? "This permanently deletes the entire event series and every occurrence." : "This permanently deletes this event."} descriptionId="delete-calendar-event-description" busy={deleting} onCancel={() => setConfirmDelete(false)} onConfirm={() => void remove()} confirmLabel={actionLabels.confirm} />}
    </AppContainer>
  );
}

export function CalendarApp({ route, user, onSignOut }: { route: string; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  if (route === "/calendar/new") return <CalendarEditor id={null} user={user} onSignOut={onSignOut} />;
  const match = /^\/calendar\/(\d+)\/edit$/.exec(route);
  return match ? <CalendarEditor id={Number(match[1])} user={user} onSignOut={onSignOut} /> : <CalendarWeek user={user} onSignOut={onSignOut} />;
}
